import { renderLucideIcon } from '../utils/lucide-icons.js'

function normalizeCapabilityAutocompleteItems(payload) {
  var items = Array.isArray(payload)
    ? payload
    : payload && Array.isArray(payload.items)
      ? payload.items
      : []

  return items
    .map(normalizeCapabilityAutocompleteItem)
    .filter(Boolean)
    .sort(function (left, right) {
      return left.shortcut.localeCompare(right.shortcut)
    })
}

function normalizeCapabilityAutocompleteItem(value) {
  if (!value || typeof value !== 'object') return null
  var shortcut = normalizeShortcut(value.shortcut || value.canonical || value.name || value.key)
  if (!shortcut) return null
  var name = String(value.name || value.marketingName || value.displayName || shortcut).replace(/^@/, '').trim()
  var displayName = String(value.displayName || (name ? '@' + name : '@' + shortcut)).trim()
  var description = String(value.description || '').trim()

  return {
    key: String(value.key || shortcut).trim(),
    name: name || displayName.replace(/^@/, ''),
    displayName: displayName,
    description: description,
    shortcut: shortcut,
    aliases: normalizeAliases(value.aliases),
    icon: String(value.icon || 'blocks').trim() || 'blocks',
    available: value.available !== false,
    unavailableReason: value.unavailableReason ? String(value.unavailableReason).trim() : ''
  }
}

function getCapabilityAutocompleteState(value, caretIndex, items) {
  var trigger = findCapabilityTrigger(value, caretIndex)
  if (!trigger) {
    return {
      open: false,
      trigger: null,
      suggestions: [],
      query: ''
    }
  }

  return {
    open: true,
    trigger: trigger,
    suggestions: filterCapabilitySuggestions(items, trigger.query),
    query: trigger.query
  }
}

function findCapabilityTrigger(value, caretIndex) {
  var text = String(value || '')
  var caret = Number.isFinite(Number(caretIndex)) ? Math.max(0, Math.min(text.length, Number(caretIndex))) : text.length
  var start = caret
  while (start > 0 && !/\s/.test(text.charAt(start - 1))) {
    start -= 1
  }

  var token = text.slice(start, caret)
  if (!/^[/@][^\s]*$/.test(token)) return null
  var marker = token.charAt(0)
  var query = token.slice(1)

  return {
    marker: marker,
    query: query,
    start: start,
    end: caret
  }
}

function filterCapabilitySuggestions(items, query) {
  var normalizedQuery = normalizeForSearch(query)
  var catalog = Array.isArray(items) ? items : []

  return catalog
    .map(function (item, index) {
      var score = scoreCapabilitySuggestion(item, normalizedQuery, index)
      return score === null ? null : { item: item, score: score }
    })
    .filter(Boolean)
    .sort(function (left, right) {
      return left.score - right.score || left.item.shortcut.localeCompare(right.item.shortcut)
    })
    .map(function (entry) {
      return entry.item
    })
}

function scoreCapabilitySuggestion(item, normalizedQuery, index) {
  if (!normalizedQuery) return index
  var candidates = [
    item.shortcut,
    item.name,
    item.displayName,
    item.key
  ].concat(item.aliases || []).map(normalizeForSearch).filter(Boolean)

  if (candidates.some(function (candidate) { return candidate === normalizedQuery })) return 0
  if (candidates.some(function (candidate) { return candidate.indexOf(normalizedQuery) === 0 })) return 1
  if (candidates.some(function (candidate) { return candidate.indexOf(normalizedQuery) !== -1 })) return 2
  return null
}

function applyCapabilityAutocompleteSelection(value, trigger, item) {
  if (!trigger || !item) return String(value || '')
  var text = String(value || '')
  var shortcut = trigger.marker + item.shortcut
  var suffix = text.slice(trigger.end)
  var separator = suffix && /^\s/.test(suffix) ? '' : ' '
  return text.slice(0, trigger.start) + shortcut + separator + suffix
}

function renderCapabilityAutocomplete(suggestions, activeIndex, labels) {
  var items = Array.isArray(suggestions) ? suggestions : []
  var normalizedLabels = normalizeLabels(labels)
  if (!items.length) {
    return (
      '<div class="wc-capability-autocomplete-empty">' +
        escapeHtml(normalizedLabels.empty) +
      '</div>'
    )
  }

  return items.map(function (item, index) {
    var disabled = item.available === false
    var reason = disabled && item.unavailableReason ? item.unavailableReason : ''
    return (
      '<button class="wc-capability-suggestion' + (index === activeIndex ? ' is-active' : '') + (disabled ? ' is-disabled' : '') + '" type="button" role="option" aria-selected="' + (index === activeIndex ? 'true' : 'false') + '" data-capability-suggestion-index="' + index + '"' + (disabled ? ' aria-disabled="true"' : '') + '>' +
        '<span class="wc-capability-suggestion-icon">' + renderLucideIcon(item.icon) + '</span>' +
        '<span class="wc-capability-suggestion-copy">' +
          '<span class="wc-capability-suggestion-name">' + escapeHtml(item.displayName || item.name) + '</span>' +
          '<span class="wc-capability-suggestion-description">' + escapeHtml(item.description) + '</span>' +
          (reason ? '<span class="wc-capability-suggestion-unavailable">' + escapeHtml(reason) + '</span>' : '') +
        '</span>' +
        '<span class="wc-capability-suggestion-shortcut">' + escapeHtml('/' + item.shortcut) + '</span>' +
      '</button>'
    )
  }).join('')
}

function normalizeShortcut(value) {
  return normalizeForSearch(value).replace(/^[/@]/, '').replace(/_/g, '-')
}

function normalizeAliases(value) {
  if (!Array.isArray(value)) return []
  return value.map(normalizeShortcut).filter(Boolean)
}

function normalizeForSearch(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()
}

function normalizeLabels(labels) {
  labels = labels || {}
  return {
    empty: labels.empty || 'Aucun agent correspondant'
  }
}

function escapeHtml(value) {
  return String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

export {
  applyCapabilityAutocompleteSelection,
  filterCapabilitySuggestions,
  findCapabilityTrigger,
  getCapabilityAutocompleteState,
  normalizeCapabilityAutocompleteItems,
  renderCapabilityAutocomplete
}
